import LogtoClient from '@logto/browser';
export { LogtoClientError, LogtoError, LogtoRequestError, OidcError, PersistKey, Prompt, ReservedScope, UserScope, buildOrganizationUrn, getOrganizationIdFromUrn, organizationUrnPrefix } from '@logto/browser';
import { readonly, inject, watchEffect } from 'vue';
import { contextInjectionKey, logtoInjectionKey } from './consts.js';
import { createContext, throwContextError } from './context.js';
import { createPluginMethods } from './plugin.js';

/**
 * Creates the Logto Vue plugin
 *
 * ```ts
 * import { createApp } from 'vue';
 * import { createLogto } from '@logto/vue';
 *
 * const app = createApp(App);
 * const app.use(createLogto, {
 *   appId: '<your-app-id>',
 *   endpoint: '<your-oidc-endpoint-domain>',
 * });
 *
 * app.mount('#app');
 * ```
 *
 * Use this in your Vue root component to register the plugin
 */
const createLogto = {
    install(app, config) {
        const client = new LogtoClient(config);
        const context = createContext(client);
        const pluginMethods = createPluginMethods(context);
        const { isAuthenticated, isLoading, error } = context;
        app.provide(contextInjectionKey, context);
        app.provide(logtoInjectionKey, {
            isAuthenticated: readonly(isAuthenticated),
            isLoading: readonly(isLoading),
            error: readonly(error),
            ...pluginMethods,
        });
    },
};
/**
 * A Vue composable method that provides the Logto reactive refs and auth methods.
 *
 * ```ts
 * import { useLogto } from '@logto/vue';
 *
 * export default {
 *  setup() {
 *    const { isAuthenticated, signIn } = useLogto();
 *
 *    return {
 *      isAuthenticated,
 *      onClickSignIn: () => {
 *        signIn('<your-redirect-uri>');
 *      },
 *    }
 *  }
 * }
 * ```
 *
 * Use this composable in the setup script of your Vue component to make sure the injection works
 */
const useLogto = () => {
    const logto = inject(logtoInjectionKey);
    if (!logto) {
        return throwContextError();
    }
    return logto;
};
/**
 * A Vue composable method that watches browser navigation and automatically handles the sign-in callback
 *
 * ```ts
 * import { useLogto } from '@logto/vue';
 * import { useHandleSignInCallback } from '@logto/vue';
 *
 * export default {
 *   setup() {
 *     useHandleSignInCallback();
 *   }
 * }
 * ```
 *
 * Use this in the setup script of your Callback page to make sure the injection works
 */
const useHandleSignInCallback = (callback) => {
    const context = inject(contextInjectionKey);
    if (!context) {
        return throwContextError();
    }
    const { isAuthenticated, isLoading, logtoClient, error } = context;
    const { handleSignInCallback } = createPluginMethods(context);
    watchEffect(async () => {
        if (!logtoClient.value) {
            return;
        }
        const currentPageUrl = window.location.href;
        const isAuthenticated = await logtoClient.value.isAuthenticated();
        const isRedirected = await logtoClient.value.isSignInRedirected(currentPageUrl);
        console.log('isAuthenticated', isAuthenticated);
        console.log('isRedirected', isRedirected);
        if (!isAuthenticated && isRedirected) {
            void handleSignInCallback(currentPageUrl, callback);
        }
    });
    return {
        isLoading: readonly(isLoading),
        isAuthenticated: readonly(isAuthenticated),
        error: readonly(error),
    };
};

export { createLogto, useHandleSignInCallback, useLogto };
